export type AiLlmProvider = "openai" | "anthropic" | "gemini";

export interface AiPurposeModel {
  provider: AiLlmProvider;
  model: string;
  temperature?: number;
  maxOutputTokens?: number;
}

export interface AiAssistantSettings {
  id: string;
  enabled: boolean;
  provider: AiLlmProvider;
  model: string;
  temperature: number;
  contextMessageLimit: number;
  systemPrompt: string;
  entityPermissions: Record<string, Record<string, boolean>>;
  purposeModels?: Record<string, AiPurposeModel>;
  updatedAt: string;
  updatedBy: string | null;
}

export interface UpdateAiAssistantSettingsRequest {
  enabled?: boolean;
  provider?: AiLlmProvider;
  model?: string;
  temperature?: number;
  contextMessageLimit?: number;
  systemPrompt?: string;
  entityPermissions?: Record<string, Record<string, boolean>>;
  purposeModels?: Record<string, AiPurposeModel>;
}

export interface EmployeeTemplate {
  id: string;
  templateId: string;
  name: string;
  title: string;
  avatar: string;
  gradient: string;
  description: string;
  specialization: string;
  personality: Record<string, unknown>;
  expertise: Record<string, unknown>;
  traits: string[];
  perfectFor: string[];
  idealFor: string[];
  skills: Record<string, unknown>;
  employeeType: string;
  promptGenerator: string;
  promptGeneratorVersion: string;
  channelPrompts: Record<string, string>;
  generatorModelPolicy: Record<string, unknown>;
  runtimeModelPolicy: Record<string, unknown>;
  schemaJson: Record<string, unknown>;
  defaultsJson: Record<string, unknown>;
  toolsPolicy: ToolsPolicy;
  supportedMessageTypes: string[];
  isActive: boolean;
  sortOrder: number;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Instance {
  id: string;
  name: string;
  displayName: string;
  domain: string;
  status: string;
  ownerEmail: string | null;
  createdAt: string;
  disabledAt: string | null;
  /** Hardcoded internal (billing-exempt) account — provisioning-api/internal-instances.js. */
  internal?: boolean;
}

// ── LLM token usage (proxied from the instance's own DB) ────────────────────
// Tokens only — no cost/USD is tracked anywhere.

export interface LlmUsageModelRow {
  model: string;
  provider: string;
  calls: number;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
}

export interface LlmUsageEmployeeRow {
  employeeName: string;
  calls: number;
  totalTokens: number;
}

export interface LlmUsageTotals {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

export interface InstanceLlmUsageSummary {
  totals: LlmUsageTotals | null;
  byModel: LlmUsageModelRow[];
  byEmployee: LlmUsageEmployeeRow[];
  /** True when the instance could not be reached (down / not yet synced). */
  unreachable?: boolean;
}

export interface InstanceLlmUsageEvent {
  id: string;
  createdAt: string;
  provider: string;
  model: string;
  purpose: string;
  status: "success" | "error" | "canceled";
  inputTokens: number | null;
  outputTokens: number | null;
  totalTokens: number | null;
  cachedTokens: number | null;
  durationMs: number | null;
  employeeId: string | null;
  employeeName: string | null;
}

export interface InstanceLlmUsageEventsPage {
  items: InstanceLlmUsageEvent[];
  total: number;
  page: number;
  limit: number;
  unreachable?: boolean;
}

// ── Voice-campaign usage (proxied from the instance's own DB) ───────────────
// Operator-only: includes provider cost, which is never shown to tenants.

export interface OutreachUsageCampaignRow {
  id: string;
  name: string;
  status: string;
  /** Accumulated provider cost in USD. */
  costTotal: number;
  /** Accumulated talk time in seconds. */
  durationTotalSeconds: number;
  attempted: number;
  connected: number;
}

export interface InstanceOutreachUsage {
  campaigns: OutreachUsageCampaignRow[];
  totals: {
    costTotal: number;
    durationTotalSeconds: number;
  } | null;
  /** True when the instance could not be reached (down / not yet synced). */
  unreachable?: boolean;
}

export interface LlmToolSummary {
  id: string;
  toolId: string;
  name: string;
  description: string;
  category: string;
}

export interface LlmTool extends LlmToolSummary {
  isActive: boolean;
  sortOrder: number;
  spec: Record<string, unknown>;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ToolsPolicy {
  allowed?: string[];
  blocked?: string[];
  rate_limits?: Record<string, unknown>;
}

export type InstancePlan = "self-hosted" | "managed";

export interface CreateInstanceRequest {
  name: string;
  email: string;
  plan?: InstancePlan;
}

export interface CreateInstanceResponse {
  message: string;
  instance: {
    name: string;
    domain: string;
    namespace: string;
    url: string;
    status: string;
  };
  user: {
    email: string;
    supabaseId: string | null;
    password: string;
  };
}

export type BillingInterval = "1 month" | "3 months" | "6 months" | "12 months";

/** 'credits' = platform keys, usage debits credits; 'byo_key' = enterprise customer key. */
export type AiBillingMode = "credits" | "byo_key";

export interface BillingConfig {
  id: string;
  instanceId: string;
  amountCents: number;
  currency: string;
  interval: BillingInterval;
  vatRateBps: number;
  description: string;
  notes: string | null;
  isActive: boolean;
  aiBillingMode: AiBillingMode;
  monthlyAllowanceCredits: number;
  collectionMethod: CollectionMethod;
  invoiceDueDays: number;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertBillingConfigRequest {
  amountCents: number;
  currency: string;
  interval: BillingInterval;
  vatRateBps: number;
  description: string;
  notes?: string | null;
  isActive?: boolean;
  aiBillingMode?: AiBillingMode;
  monthlyAllowanceCredits?: number;
  collectionMethod?: CollectionMethod;
  invoiceDueDays?: number;
  /** Save a VAT change although a subscription is running (not stored). */
  confirmVatChange?: boolean;
}

/**
 * How the subscription collects money. 'send_invoice' is the bank-transfer
 * path — Stripe issues an invoice with a virtual IBAN rather than charging a
 * stored method, because bank transfers are not supported in Checkout
 * subscription mode.
 */
export type CollectionMethod = "charge_automatically" | "send_invoice";

export interface InstanceSubscription {
  id: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  status: string;
  cancelAtPeriodEnd?: boolean;
  startedAt: string | null;
  canceledAt: string | null;
  nextPaymentAt: string | null;
  currentPeriodEnd: string | null;
  gracePeriodEndsAt: string | null;
  /** Stripe answered resource_missing for the stored subscription (other-mode leftover). */
  stripeRefMissing?: boolean;
}

export interface InstancePayment {
  id: string;
  stripeInvoiceId: string;
  sequenceType: string | null;
  amountCents: number;
  currency: string;
  status: string;
  description: string | null;
  paidAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  invoicePdfUrl: string | null;
  hostedInvoiceUrl?: string | null;
  failureReason: string | null;
  paidOutOfBand?: boolean;
  oobMethod?: OobMethod | null;
  lastReminderAt?: string | null;
  /** Gross sum of the invoice's issued credit notes. */
  creditedCents?: number;
  /** Money returned through Stripe (charge refunds). */
  refundedCents?: number;
  creditNotePdfUrl?: string | null;
  createdAt: string;
}

/** Keyset-paginated payment history (GET /api/admin/instances/:name/payments). */
export interface PaymentsListResponse {
  items: InstancePayment[];
  nextCursor: string | null;
}

export interface ProvisioningStep {
  status: "done" | "failed";
  at: string;
  error?: string;
  skipped?: string;
}

export interface InstanceDetail {
  instance: {
    id: string;
    name: string;
    displayName: string;
    domain: string;
    status: string;
    disabledAt: string | null;
    scheduledDeletionAt: string | null;
    provisioningSteps: Record<string, ProvisioningStep> | null;
    helmInstall: { success: boolean; runUrl: string | null; at: string } | null;
    /** Billing-exempt account: a plain billing reset does not block it. */
    internal?: boolean;
    /** Last VAT check of the Stripe customer. */
    vatCheck?: VatCheck | null;
    /** Affonso partner code + attribution status. */
    partner?: InstancePartner;
    /** BZSt qualified VAT id confirmations (newest last, max 5). */
    vatConfirmations?: VatConfirmation[];
  };
  config: BillingConfig | null;
  subscription: InstanceSubscription | null;
  lastPayment: InstancePayment | null;
}

// ---------------------------------------------------------------------------
// Billing catalog (rate cards, model prices, credit packs, credit grants)
// ---------------------------------------------------------------------------

export interface RateCardEntry {
  employeeType: string;
  actionType: string;
  credits: number;
}

export interface RateCard {
  id: string;
  version: number;
  status: "draft" | "published";
  effectiveFrom: string | null;
  createdBy: string | null;
  createdAt: string;
  entries: RateCardEntry[];
}

export interface RateCardVersionSummary {
  id: string;
  version: number;
  status: "draft" | "published";
  effectiveFrom: string | null;
  createdBy: string | null;
  createdAt: string;
  entryCount: number;
}

export interface RateCardResponse {
  rateCard: RateCard | null;
  versions: RateCardVersionSummary[];
}

export interface ModelPrice {
  id: string;
  provider: string;
  model: string;
  inputEurPer1m: number;
  outputEurPer1m: number;
  cachedInputEurPer1m: number | null;
  effectiveFrom: string;
  createdAt: string;
}

export interface UpsertModelPriceRequest {
  provider: string;
  model: string;
  inputEurPer1m: number;
  outputEurPer1m: number;
  cachedInputEurPer1m?: number | null;
  effectiveFrom: string;
}

export interface CreditPack {
  id: string;
  code: string;
  name: string;
  credits: number;
  amountCents: number;
  currency: string;
  expiresInMonths: number | null;
  active: boolean;
  sort: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertCreditPackRequest {
  code: string;
  name: string;
  credits: number;
  amountCents: number;
  currency?: string;
  expiresInMonths?: number | null;
  active?: boolean;
  sort?: number;
}

export type CreditGrantKind = "allowance" | "topup" | "manual";

export interface CreditGrant {
  id: string;
  instanceId: string;
  kind: CreditGrantKind;
  credits: number;
  periodStart: string | null;
  periodEnd: string | null;
  expiresAt: string | null;
  sourcePaymentId: string | null;
  externalRef: string | null;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
  appliedAt: string | null;
  applyError: string | null;
}

// ── Margin monitoring (B6) ───────────────────────────────────────────────────

export type MarginGroupBy = "instance" | "employee_type" | "action_type";

/** One aggregated table row, grouped by the selected dimension. */
export interface MarginRow {
  key: string;
  actionCount: number;
  creditsCharged: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  costEur: number;
  revenueEur: number;
  /** null when there is no revenue (divide-by-zero guard). */
  marginPct: number | null;
  /** Only meaningful when groupBy === 'instance'. */
  byoKey: boolean;
  /** Below the platform low-margin threshold and not byo — highlight. */
  lowMargin: boolean;
}

export interface MarginInstance {
  instanceId: string;
  instanceName: string;
  aiBillingMode: AiBillingMode;
  byoKey: boolean;
  actionCount: number;
  creditsCharged: number;
  costEur: number;
  revenueEur: number;
  marginPct: number | null;
  unpricedActionCount: number;
  lastReceivedAt: string | null;
}

export interface MarginLowMarginAlert {
  instanceName: string;
  marginPct: number | null;
  revenueEur: number;
  costEur: number;
}

export interface MarginStaleAlert {
  instanceName: string;
  lastReceivedAt: string | null;
}

export interface MarginUnpricedModel {
  provider: string;
  model: string;
  actionCount: number;
  tokens: number;
}

export interface MarginAlerts {
  lowMargin: MarginLowMarginAlert[];
  stale: MarginStaleAlert[];
  unpricedModels: MarginUnpricedModel[];
}

export interface MarginReportResponse {
  from: string;
  to: string;
  groupBy: MarginGroupBy;
  creditUnitValueEur: number;
  lowMarginThresholdPct: number;
  rows: MarginRow[];
  instances: MarginInstance[];
  alerts: MarginAlerts;
}

/** 422 body from the billing-config PUT when switching to byo_key without keys. */
export interface NoInstanceLlmKeysError {
  code: "NO_INSTANCE_LLM_KEYS";
  error: string;
  keyHints: string[];
}

export const AI_ASSISTANT_ENTITIES = [
  "deal",
  "account",
  "task",
  "tracker_task",
  "calendar_event",
  "invoice",
  "product",
  "pipeline",
  "resource",
  "inventory",
  "location",
  "employee",
  "goal",
  "project",
  "knowledge_article",
  "knowledge_category",
] as const;

export const AI_CRUD_ACTIONS = ["create", "read", "update", "delete"] as const;

export type AiAssistantEntity = (typeof AI_ASSISTANT_ENTITIES)[number];
export type AiCrudAction = (typeof AI_CRUD_ACTIONS)[number];

// ---------------------------------------------------------------------------
// Centrally-managed per-instance AI employees (Phase 2)
// ---------------------------------------------------------------------------

export type InstanceEmployeeSyncStatus =
  | "draft"
  | "pending"
  | "applied"
  | "failed"
  | "archived";

export interface InstanceEmployee {
  id: string;
  instanceId: string;
  sourceTemplateId: string | null;
  name: string;
  employeeType: string;
  isActive: boolean;
  config: Record<string, unknown>;
  promptGenerator: string | null;
  version: number;
  appliedVersion: number | null;
  syncStatus: InstanceEmployeeSyncStatus;
  syncError: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateInstanceEmployeeRequest {
  name?: string;
  employeeType?: string;
  config?: Record<string, unknown>;
  promptGenerator?: string | null;
}

export interface CreateInstanceEmployeeRequest {
  name: string;
  sourceTemplateId?: string;
  employeeType?: string;
  config?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Operator billing operations (apply changes to Stripe)
// ---------------------------------------------------------------------------

/**
 * POST /api/admin/instances/:name/billing/apply-amount-change
 * Applies a new recurring amount to the live subscription immediately, with
 * proration; the client is charged the prorated difference now.
 */
export interface ApplyAmountChangeRequest {
  newAmountCents: number;
  setupFeeCents?: number;
  idemKey?: string;
}

export interface ApplyAmountChangeResponse {
  ok: true;
  subscriptionId: string;
  latestInvoiceId: string;
}

/** One line item on a one-off invoice. */
export interface OneOffInvoiceLine {
  description: string;
  /** Unit of measure (Std, Stk, Monat, …). */
  unit?: string;
  /** Per-unit price in minor units, net. Negative => a discount / credit line. */
  amountCents: number;
  quantity?: number;
  /** Per-line VAT in basis points (1900 = 19%). Absent => the config rate. */
  vatRateBps?: number;
}

export type ReverseChargeMode = "none" | "de_13b" | "eu_b2b";

/** POST /api/admin/instances/:name/billing/one-off-invoice */
export interface OneOffInvoiceRequest {
  lines: OneOffInvoiceLine[];
  /** Amounts entered gross (VAT-inclusive) when true. */
  taxInclusive?: boolean;
  /** Reverse charge: 0% VAT + a legal footer. Overrides per-line VAT. */
  reverseCharge?: ReverseChargeMode;
  /** Customer VAT id (USt-IdNr.). */
  customerVatId?: string;
  servicePeriod?: string;
  poNumber?: string;
  note?: string;
  /** Finalize immediately (auto-advance). Default: create a draft for review. */
  finalize?: boolean;
  idemKey?: string;
  dueDays?: number;
}

export interface OneOffInvoiceResponse {
  ok: true;
  invoiceId: string;
  collectionMethod: "charge_automatically" | "send_invoice";
  status: string | null;
  finalized: boolean;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

// ---------------------------------------------------------------------------
// Payments registry + billing audit log (GET /api/admin/billing/...)
// ---------------------------------------------------------------------------

/** Registry status (Stripe/accounting view); "overdue" is a filter, not a status. */
export type RegistryInvoiceStatus =
  | "paid"
  | "partially_refunded"
  | "refunded"
  | "open"
  | "void"
  | "uncollectible";
export type RegistryStatusFilter = RegistryInvoiceStatus | "overdue";
export type InvoiceKind = "subscription" | "one_off";

export interface RegistryInvoice {
  id: string;
  stripeInvoiceId: string;
  invoiceNumber: string | null;
  instanceName: string;
  kind: InvoiceKind | null;
  status: RegistryInvoiceStatus;
  paymentStatus: string;
  createdAt: string;
  dueDate: string | null;
  /** Whole days past the due date; null unless the invoice is overdue. */
  overdueDays: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  subtotalCents: number | null;
  taxCents: number | null;
  /** Gross (VAT inclusive). */
  totalCents: number;
  amountPaidCents: number | null;
  currency: string;
  paidAt: string | null;
  paidOutOfBand: boolean;
  oobMethod: string | null;
  oobReference: string | null;
  oobPaidOn: string | null;
  lastReminderAt: string | null;
  /** Gross sum of the invoice's issued credit notes. */
  creditedCents: number;
  /** Money returned through Stripe (charge refunds). */
  refundedCents: number;
  creditNotePdfUrl: string | null;
  collectionMethod: CollectionMethod | null;
  method: string | null;
  invoicePdfUrl: string | null;
  hostedInvoiceUrl: string | null;
}

export interface RegistryInvoiceFilters {
  status?: RegistryStatusFilter;
  instance?: string;
  from?: string;
  to?: string;
  kind?: InvoiceKind;
  oob?: boolean;
}

export interface RegistryInvoicesResponse {
  items: RegistryInvoice[];
  nextCursor: string | null;
  /** paidCents is net of money given back; refundedCents is that money. */
  totals: { paidCents: number; refundedCents: number; openCents: number; overdueCents: number };
}

export interface RegistrySubscription {
  instanceName: string;
  instanceStatus: string;
  amountCents: number | null;
  currency: string | null;
  interval: BillingInterval | null;
  collectionMethod: CollectionMethod | null;
  configActive: boolean | null;
  subscriptionStatus: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  stripeRefMissing: boolean;
  paidThrough: string | null;
  debtCents: number;
  overdueCents: number;
}

export interface RegistrySubscriptionsResponse {
  items: RegistrySubscription[];
  summary: { mrrCents: number; receivablesCents: number };
}

export interface BillingAuditEntry {
  id: string;
  instanceName: string | null;
  actor: string;
  action: string;
  stripeObjectId: string | null;
  amountCents: number | null;
  details: Record<string, unknown>;
  createdAt: string;
}

export interface BillingAuditResponse {
  items: BillingAuditEntry[];
  nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// Operator billing actions (POST /api/admin/billing/invoices/:id/...,
// POST /api/admin/instances/:name/billing/...)
// ---------------------------------------------------------------------------

/** How an invoice was settled outside Stripe. */
export type OobMethod = "bank_transfer" | "cash" | "other";

export interface MarkInvoicePaidRequest {
  method: OobMethod;
  /** YYYY-MM-DD, not in the future. */
  paidOn: string;
  reference?: string;
  note?: string;
}

export interface MarkInvoicePaidResponse {
  ok: true;
  invoiceId: string;
  status: string;
  subscriptionStatus: string | null;
}

export type CreditNoteReason = "duplicate" | "fraudulent" | "order_change" | "product_unsatisfactory";

/** POST /api/admin/billing/invoices/:id/credit-note[/preview]. */
export interface CreditNoteRequest {
  /** Gross; omitted = everything still creditable. */
  amountCents?: number;
  reason: CreditNoteReason;
  memo?: string;
  /** true = refund to the original payment method; false = returned outside Stripe. */
  refund: boolean;
}

export interface CreditNotePreviewResponse {
  ok: true;
  invoiceId: string;
  currency: string;
  invoiceTotalCents: number;
  remainingCents: number;
  /** Whether Stripe holds the money (false for invoices paid outside Stripe). */
  refundPossible: boolean;
  totalCents: number;
  netCents: number;
  taxCents: number;
  refundAmountCents: number;
  outOfBandAmountCents: number;
}

export interface CreditNoteResponse {
  ok: true;
  invoiceId: string;
  creditNoteId: string;
  creditNoteNumber: string | null;
  creditNotePdfUrl: string | null;
  totalCents: number;
  refundAmountCents: number;
  outOfBandAmountCents: number;
}

export interface VoidInvoiceResponse {
  ok: true;
  invoiceId: string;
  status: string;
  subscriptionStatus: string | null;
}

export interface RemindInvoiceResponse {
  ok: true;
  invoiceId: string;
  /** 'sent' = invoice e-mail re-sent; 'charged' = card/SEPA charge retried successfully. */
  action: "sent" | "charged";
  lastReminderAt: string;
}

export interface AdminSubscribeRequest {
  idemKey?: string;
}

export interface AdminSubscribeResponse {
  ok: true;
  subscriptionId: string;
  invoiceId: string | null;
  hostedInvoiceUrl: string | null;
}

export type CancelSubscriptionMode = "period_end" | "immediate";

export interface AdminCancelSubscriptionResponse {
  ok: true;
  subscriptionId: string;
  status: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface PortalLinkResponse {
  ok: true;
  url: string;
}

/** POST /api/admin/instances/:name/billing/reset */
export interface BillingResetRequest {
  /** The instance name, typed back by the operator. */
  confirm: string;
}

/**
 * POST /api/admin/instances/:name/billing/reset. A non-internal instance needs
 * `resubscribe` (reset + new send_invoice subscription in one request) or
 * `allowBlock` (accept that the billing gate blocks it) — else 409
 * RESET_WOULD_BLOCK.
 */
export interface BillingResetRequest {
  confirm: string;
  resubscribe?: boolean;
  allowBlock?: boolean;
  idemKey?: string;
}

export interface BillingResetResponse {
  ok: true;
  reason: "other_mode" | "canceled";
  archivedPayments: number;
  /** Present when the reset resubscribed by invoice. */
  subscriptionId?: string;
  invoiceId?: string | null;
  hostedInvoiceUrl?: string | null;
}

/**
 * GET /api/admin/instances/:name/billing/customer — the Stripe customer's
 * billing email. `isFallback` = it is STRIPE_FALLBACK_BILLING_EMAIL, so SEPA
 * notices, receipts and invoices go to the operator instead of the client.
 */
export interface BillingCustomerEmailResponse {
  /** Stripe dashboard link, test or live by the customer's mode. */
  dashboardUrl?: string | null;
  customerId: string | null;
  email: string | null;
  isFallback: boolean;
  deleted: boolean;
}

export interface StripeSyncResponse {
  ok: true;
  subscriptionStatus: string;
  invoicesSeen: number;
  invoicesRecorded: number;
  invoicesSkipped: number;
}

// ---------------------------------------------------------------------------
// Stripe customer of an instance: link, VAT check, partner (Affonso)
// ---------------------------------------------------------------------------

export type TaxTreatment = "de_vat" | "reverse_charge" | "non_eu_not_taxable";

export type VatConfirmationOutcome =
  | "valid"
  | "valid_simple"
  | "partial"
  | "valid_special"
  | "mismatch"
  | "invalid"
  | "unavailable"
  | "error"
  | "not_requested";

/** One BZSt eVatR qualified confirmation (instances.metadata.vat_confirmations[]). */
export interface VatConfirmation {
  at: string;
  vat_id: string;
  requester_vat_id?: string;
  outcome: VatConfirmationOutcome;
  status: string | null;
  http_status: number | null;
  /** A = matches, B = does not match, C = not asked, D = not provided by the member state. */
  matches: { company: string | null; street: string | null; zip: string | null; city: string | null };
  /** Details sent with the request (the A–D letters refer to these). */
  request?: { firmenname?: string; ort?: string; plz?: string; strasse?: string } | null;
  error: string | null;
  raw?: unknown;
  by?: string;
}

/** Last VAT check (instances.metadata.vat_check). */
export interface VatCheck {
  at: string;
  by: string;
  customer_id: string;
  country: string | null;
  tax_treatment: TaxTreatment;
  vat_rate_bps: number;
  tax_review: string | null;
  customer_vat_id: string | null;
  vat_confirmation_outcome: VatConfirmationOutcome | null;
  applied: boolean;
}

export type PartnerAttributionStatus =
  | "attributed"
  | "already_exists"
  | "affiliate_not_found"
  | "pending_customer"
  | "no_email"
  | "not_configured"
  | "failed";

/** instances.metadata.affonso_attribution */
export interface PartnerAttribution {
  status: PartnerAttributionStatus;
  code: string;
  customer_id?: string | null;
  affiliate_id?: string | null;
  affiliate_name?: string | null;
  referral_id?: string | null;
  http_status?: number | null;
  error?: string | null;
  at: string;
}

export interface InstancePartner {
  affonsoReferral: string | null;
  attribution: PartnerAttribution | null;
}

export interface LinkStripeCustomerRequest {
  stripeCustomerId: string;
  force?: boolean;
}

export interface LinkStripeCustomerResponse {
  ok: true;
  customer: {
    id: string;
    email: string | null;
    name: string | null;
    country: string | null;
    livemode: boolean;
    dashboardUrl: string;
  };
  replacedCustomerId: string | null;
  partnerAttribution: PartnerAttribution | null;
  warnings?: string[];
}

export interface VatCheckResponse {
  ok: true;
  tax: {
    vatRateBps: number;
    treatment: TaxTreatment;
    reviewNeeded: boolean;
    reason: string | null;
    country: string | null;
    vatId: string | null;
  };
  vatConfirmation: (Partial<VatConfirmation> & { outcome: VatConfirmationOutcome }) | null;
  applied: boolean;
  warnings: string[];
}

export interface SetPartnerResponse {
  ok: true;
  affonsoReferral: string | null;
  partnerAttribution: PartnerAttribution | null;
}
